import { useEffect, useState } from "react";
import { Megaphone, Send, Trash2, EyeOff, Eye } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import ConfirmDialog from "@/components/ConfirmDialog";

const empty = { title: "", body: "", link: "", link_label: "" };

/** Coach-side composer: post one pinned announcement to every student's dashboard. */
export const AnnouncementManager = () => {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(empty);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/announcements");
      setRows(data);
    } catch (e) {
      /* non-blocking */
    }
  };

  useEffect(() => { load(); }, []);

  const post = async () => {
    if (!form.title.trim() || !form.body.trim()) {
      toast.error("Add a title and a message");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/announcements", form);
      toast.success("Posted — students get a dashboard banner and a phone alert");
      setForm(empty);
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not post");
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (a) => {
    try {
      await api.patch(`/admin/announcements/${a.id}`, { active: !a.active });
      await load();
    } catch (e) {
      toast.error("Could not update");
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/admin/announcements/${confirmDelete.id}`);
      setConfirmDelete(null);
      await load();
      toast.success("Deleted");
    } catch (e) {
      toast.error("Could not delete");
    }
  };

  const live = rows.find((r) => r.active);

  return (
    <div className="surface-card p-5 mb-10" data-testid="announcement-manager">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-center gap-2">
          <Megaphone size={15} style={{ color: "var(--lime)" }} />
          <span className="font-mono text-xs uppercase tracking-wider" style={{ color: "var(--lime)" }}>
            Dashboard announcement
          </span>
        </div>
        <button
          onClick={() => setOpen((v) => !v)}
          className="btn-ghost text-sm"
          data-testid="announcement-compose-toggle"
        >
          {open ? "Cancel" : live ? "Post new" : "Write one"}
        </button>
      </div>

      {live ? (
        <div className="text-sm mb-1" data-testid="announcement-live">
          <span className="font-semibold">{live.title}</span>
          <span style={{ color: "var(--text-muted)" }}>, showing to all students</span>
        </div>
      ) : (
        <p className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>
          Nothing pinned right now. Posting one replaces whatever is live and pings every student&apos;s phone.
        </p>
      )}

      {open && (
        <div className="mt-4 space-y-3">
          <input
            className="input-base w-full"
            placeholder="Title (e.g. Live deal review this Thursday)"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            data-testid="announcement-title-input"
          />
          <textarea
            className="input-base w-full min-h-[100px]"
            placeholder="What do you want every student to know?"
            value={form.body}
            onChange={(e) => setForm({ ...form, body: e.target.value })}
            data-testid="announcement-body-input"
          />
          <div className="grid sm:grid-cols-2 gap-3">
            <input
              className="input-base w-full"
              placeholder="Link (optional)"
              value={form.link}
              onChange={(e) => setForm({ ...form, link: e.target.value })}
              data-testid="announcement-link-input"
            />
            <input
              className="input-base w-full"
              placeholder="Link button text (optional)"
              value={form.link_label}
              onChange={(e) => setForm({ ...form, link_label: e.target.value })}
              data-testid="announcement-link-label-input"
            />
          </div>
          <button
            onClick={post}
            disabled={busy}
            className="btn-primary text-sm flex items-center gap-2"
            data-testid="announcement-post-button"
          >
            {busy ? <span className="spinner" /> : <Send size={14} />} Post announcement
          </button>
        </div>
      )}

      {rows.length > 0 && (
        <div className="mt-5 space-y-2">
          {rows.map((a) => (
            <div
              key={a.id}
              className="flex items-center gap-3 p-3 rounded-2xl"
              style={{
                background: "var(--surface2)",
                border: a.active ? "1px solid var(--border-lime)" : "1px solid var(--border)",
              }}
              data-testid={`announcement-row-${a.id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{a.title}</div>
                <div className="text-xs font-mono truncate" style={{ color: "var(--text-muted)" }}>
                  {new Date(a.created_at).toLocaleDateString()} {a.active ? "· live" : "· hidden"}
                </div>
              </div>
              <button
                onClick={() => toggle(a)}
                className="p-2 rounded-xl hover:bg-white/5 transition"
                style={{ color: "var(--text-muted)" }}
                title={a.active ? "Hide from students" : "Show to students"}
                data-testid={`announcement-toggle-${a.id}`}
              >
                {a.active ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
              <button
                onClick={() => setConfirmDelete(a)}
                className="p-2 rounded-xl hover:bg-white/5 transition"
                style={{ color: "var(--danger, #ef4444)" }}
                title="Delete"
                data-testid={`announcement-delete-${a.id}`}
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={!!confirmDelete}
        title="Delete this announcement?"
        description={`"${confirmDelete?.title || ""}" will be removed for good.`}
        destructive
        confirmLabel="Delete"
        onCancel={() => setConfirmDelete(null)}
        onConfirm={remove}
      />
    </div>
  );
};

export default AnnouncementManager;
